import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Cookie, X, Settings, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ConsentState {
  necessary: boolean;
  functional: boolean;
  timestamp: string;
  version: string;
}

const CONSENT_VERSION = "1.0";
const CONSENT_KEY = "cookie-consent";

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [functionalEnabled, setFunctionalEnabled] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (includeFunctional: boolean) => {
    const consentData: ConsentState = {
      necessary: true,
      functional: includeFunctional,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent(true);
  };

  const handleAcceptNecessary = () => {
    saveConsent(false);
  };

  const handleSavePreferences = () => {
    saveConsent(functionalEnabled);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-2xl animate-slide-in-from-bottom">
        <Card className="shadow-2xl border-border/50 bg-card/95 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 bg-primary/10 rounded-lg">
                <Cookie className="h-6 w-6 text-primary" />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Cookie-Einstellungen
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Wir verwenden Cookies, um Ihnen die beste Nutzererfahrung zu bieten. 
                    Notwendige Cookies sind für die Grundfunktionen erforderlich.
                  </p>
                </div>

                {showDetails && (
                  <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Notwendige Cookies</p>
                        <p className="text-xs text-muted-foreground">
                          Authentifizierung und Sicherheit
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Check className="h-4 w-4 text-success" />
                        <span className="text-xs">Immer aktiv</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Funktionale Cookies</p>
                        <p className="text-xs text-muted-foreground">
                          Sidebar-Status, Einstellungen
                        </p>
                      </div>
                      <button
                        onClick={() => setFunctionalEnabled(!functionalEnabled)}
                        className={cn(
                          "relative w-10 h-5 rounded-full transition-colors",
                          functionalEnabled ? "bg-success" : "bg-muted-foreground/30"
                        )}
                      >
                        <span
                          className={cn(
                            "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform",
                            functionalEnabled && "translate-x-5"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="bg-success hover:bg-success/90"
                  >
                    Alle akzeptieren
                  </Button>
                  
                  {showDetails ? (
                    <Button
                      variant="outline"
                      onClick={handleSavePreferences}
                    >
                      Einstellungen speichern
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleAcceptNecessary}
                    >
                      Nur notwendige
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-muted-foreground"
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    {showDetails ? "Weniger" : "Einstellungen"}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">
                  Mehr Informationen in unserer{" "}
                  <a href="/datenschutz" className="text-primary hover:underline">
                    Datenschutzerklärung
                  </a>
                </p>
              </div>

              <button
                onClick={handleAcceptNecessary}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Schliessen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Helper function to check consent
export function hasConsentFor(type: "functional"): boolean {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;
    const parsed: ConsentState = JSON.parse(consent);
    return parsed[type] === true;
  } catch {
    return false;
  }
}

// Helper to reset consent (for settings page)
export function resetCookieConsent(): void {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
}
